'use client';

import React, { useEffect, useMemo } from 'react';

import { FundingKey, FundingType, ProductModel } from '@bytel/leviator';
import { DirectionEnum, FlexBox, FlexItem, GapSize, Justify } from '@bytel/trilogy-react';

import { CategoryPlanType, MobilePlanType } from '@app-types/plan';
import { PriceFundingsDetails } from '@app-types/product';

import { SSIBlock } from '@components/ssi-block';

import { partials } from '@helpers/partials';
import { LockedPlanValue, initBlockedPlans } from '@helpers/plan-lock';

import { MarketingType, PlanSubTypeId, PlanTypeId } from '@constants/plan';

import { CategoryTemplate } from './category-template';
import { OfferCardContainer } from './offer-card-container';
import { PhoneInCart } from './phone-in-cart';

type OfferCategoryListProps = {
    category: CategoryPlanType;
    openOfferDetails: (offer: MobilePlanType) => void;
    currentCategory: PlanTypeId;
    phoneInCart?: { phone: ProductModel; details?: PriceFundingsDetails };
    onAddToCart: (
        selectedOffer: MobilePlanType,
        fundingKey?: FundingKey,
        fromUpgradeModal?: boolean,
        redirectToSmartphonesWall?: boolean,
    ) => Promise<void>;
    isConnected: boolean;
    isPhoneLoading: boolean;
    isSmallPriceCategory: boolean;
};

export function OfferCategoryListing({
    category,
    openOfferDetails,
    currentCategory,
    phoneInCart,
    onAddToCart,
    isConnected,
    isPhoneLoading,
    isSmallPriceCategory,
}: Readonly<OfferCategoryListProps>) {
    const cashFunding = phoneInCart?.details?.fundings?.find((f) => f.type === FundingType.COMPTANT);

    const hasHatInLine = useMemo(
        () => category.products.some((p) => Boolean(p.planFlag || p.isOwned)),
        [category.products],
    );

    const hasBlockedPlan = useMemo(
        () => category.products.some((p) => p.locked !== LockedPlanValue.UNLOCK),
        [category.products],
    );

    useEffect(() => {
        initBlockedPlans(category);
    }, []);

    const hasOnlySmartChangePlans = useMemo(
        () => !category.products.some((p) => p.subTypeId !== PlanSubTypeId.SMART_CHANGE),
        [category.products],
    );

    const hasCategoryDiscount = useMemo(
        () =>
            category.products.some((p) =>
                p.details?.discounts?.some(
                    (d) =>
                        (d.marketingTypes?.includes(MarketingType.HOME) && !d.isIncitation) ||
                        d.marketingTypes?.includes(MarketingType.MULTILINE),
                ),
            ),
        [category.products],
    );
    const hasBigInnerHeight = hasCategoryDiscount && category.products.some((p) => p.cmsContent?.bigBenefit);

    if (category.products.length === 0) {
        return null;
    }

    return (
        <FlexItem id={category.code}>
            <CategoryTemplate
                hasOnlySmartChangePlans={hasOnlySmartChangePlans}
                isSmallPriceCategory={isSmallPriceCategory}
                categorySubtitle={category?.subtitle}
            >
                <FlexBox direction={DirectionEnum.COLUMN}>
                    {currentCategory === PlanTypeId.SIMO && category.products[0] && (
                        <PhoneInCart
                            phone={phoneInCart}
                            isFloating
                            selectedFunding={cashFunding}
                            plan={category.products[0]}
                        />
                    )}
                    <FlexItem>
                        <FlexBox gap={GapSize.SIX} justify={Justify.CENTER} wrap>
                            {category.products
                                .filter(
                                    (plan) =>
                                        !hasOnlySmartChangePlans || (plan.simulatedCart?.fundings ?? []).length > 0,
                                )
                                .map((plan) => (
                                    <OfferCardContainer
                                        key={plan.gencode}
                                        openOfferDetails={openOfferDetails}
                                        phoneInCart={phoneInCart}
                                        plan={plan}
                                        isPhoneLoading={isPhoneLoading}
                                        onAddToCart={onAddToCart}
                                        isConnected={isConnected}
                                        isSmallPrice={isSmallPriceCategory}
                                        hatInLine={hasHatInLine}
                                        showBlockedPlanZone={hasBlockedPlan}
                                        hasBigInnerHeight={hasBigInnerHeight}
                                    />
                                ))}
                        </FlexBox>
                    </FlexItem>
                </FlexBox>
                {isSmallPriceCategory && <SSIBlock url={partials.MARKETING.FORFAIT_MOINS_26} />}
                {isSmallPriceCategory && <SSIBlock url={partials.MARKETING.KIDS_WATCH} />}
            </CategoryTemplate>
        </FlexItem>
    );
}
